import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardDocumentCheckIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { fetchChecklistList } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

const categoryLabels: Record<string, string> = {
  'life-event': 'Life Events',
  'room-by-room': 'Room by Room',
  caregiver: 'Caregiver',
  seasonal: 'Seasonal',
  'condition-specific': 'Condition-Specific',
};

const categoryOrder = ['life-event', 'room-by-room', 'caregiver', 'seasonal', 'condition-specific'];

const ChecklistIndex = () => {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['checklist-list'],
    queryFn: fetchChecklistList,
    staleTime: 15 * 60 * 1000,
  });

  const grouped = (entries || []).reduce<Record<string, typeof entries>>((acc, entry) => {
    const cat = entry.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(entry);
    return acc;
  }, {});

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'Checklists', url: `${BASE_URL}/checklists` },
  ];

  return (
    <>
      <SEO
        title="Free Senior Safety & Caregiving Checklists"
        description="Free printable checklists for senior home safety, caregiving, and family coordination. Organized by the three pillars: Safety, Wellness, and Connection."
        keywords="senior safety checklist, caregiver checklist, home safety checklist for seniors, fall prevention checklist, hospital discharge checklist"
        url={`${BASE_URL}/checklists`}
        canonical={`${BASE_URL}/checklists`}
        breadcrumbs={breadcrumbs}
      />

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <header className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full dark:bg-green-950/50 mb-6">
              <ClipboardDocumentCheckIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Checklists</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Free, printable checklists for senior home safety, caregiving, and family coordination. Each one covers Safety, Wellness, and Connection.
            </p>
          </header>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : (
            categoryOrder
              .filter((cat) => grouped[cat]?.length)
              .map((cat) => (
                <section key={cat} className="mb-12">
                  <h2 className="text-2xl font-bold text-foreground mb-6 border-b pb-3">
                    {categoryLabels[cat] || cat}
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {grouped[cat]!.map((entry) => (
                      <Link
                        key={entry.slug}
                        to={`/checklists/${entry.slug}`}
                        className="group flex flex-col p-5 rounded-xl bg-muted/50 hover:bg-muted transition-colors border border-transparent hover:border-primary/20"
                      >
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                          {entry.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                          {entry.situation}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            {categoryLabels[entry.category] || entry.category}
                          </Badge>
                          {entry.printable && (
                            <span className="inline-flex items-center text-xs text-muted-foreground">
                              <PrinterIcon className="w-3 h-3 mr-1" />
                              Printable
                            </span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ))
          )}
        </div>
      </div>
    </>
  );
};

export default ChecklistIndex;
